import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DashboardLayout from "../../components/DashboardLayout";
import FormModal from "../../components/FormModal";
import { Plus, Edit, Trash2, Image as ImageIcon } from "lucide-react";
import { useForm } from "@inertiajs/react";

interface Brand {
  id: number;
  name: string;
  image_url?: string;
}

interface BrandsProps {
  brands: Brand[];
  store_logo?: string;
  store_name?: string;
}

const Brands = ({ brands, store_logo, store_name }: BrandsProps) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const {
    data,
    setData,
    post,
    delete: destroy,
    put,
    processing,
    errors,
    reset,
    transform,
  } = useForm({
    name: "",
    image: null as File | null,
  });

  transform((data) => ({
    brand: {
      ...data,
    },
  }));

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setData({
      name: brand.name,
      image: null,
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingBrand(null);
    reset();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBrand) {
      put(`/admin/brands/${editingBrand.id}`, {
        onSuccess: () => handleClose(),
      });
    } else {
      post("/admin/brands", {
        onSuccess: () => handleClose(),
      });
    }
  };

  return (
    <DashboardLayout title="Gestión de Marcas" storeLogo={store_logo} storeName={store_name}>
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Gestión de Marcas
            </h1>
            <p className="text-slate-50 text-lg">
              Añade y gestiona tus marcas
            </p>
          </div>
          <Button onClick={() => setModalOpen(true)} className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200">
            <Plus className="mr-2 h-5 w-5" />
            Nueva Marca
          </Button>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={editingBrand ? "Editar Marca" : "Nueva Marca"}
        onSubmit={handleSubmit}
        isProcessing={processing}
      >
        <div className="grid w-full items-center gap-4">
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
            />
            {errors.name && <div className="text-red-500">{errors.name}</div>}
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="image">Imagen</Label>
            <Input
              id="image"
              type="file"
              onChange={(e) =>
                setData("image", e.target.files ? e.target.files[0] : null)
              }
            />
            {errors.image && <div className="text-red-500">{errors.image}</div>}
          </div>
        </div>
      </FormModal>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {brands.map((brand, index) => (
          <Card key={brand.id} className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
              index % 3 === 0 ? 'from-rose-500/5 via-pink-500/5 to-red-500/5' :
              index % 3 === 1 ? 'from-orange-500/5 via-yellow-500/5 to-amber-500/5' :
              'from-purple-500/5 via-violet-500/5 to-indigo-500/5'
            }`} />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 relative z-10">
              <CardTitle className="text-lg font-bold text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">
                {brand.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-10">
              <div className="flex justify-center mb-6">
                {brand.image_url ? (
                  <img
                    src={brand.image_url}
                    alt={brand.name}
                    className="h-24 w-24 object-contain rounded-lg shadow-sm group-hover:shadow-md transition-shadow"
                  />
                ) : (
                  <div className="p-4 rounded-lg bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600">
                    <ImageIcon className="h-16 w-16 text-slate-400 dark:text-slate-500" />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end">
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(brand)}
                    className="hover:bg-blue-50 dark:hover:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-200"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      confirm(`¿Estás seguro de que quieres eliminar la marca "${brand.name}"? Esta acción no se puede deshacer.`) &&
                      destroy(`/admin/brands/${brand.id}`, { method: "delete" })
                    }
                    className="hover:bg-red-50 dark:hover:bg-red-900/20 border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:border-red-400 dark:hover:border-red-500 transition-all duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {brands.length === 0 && (
        <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 mb-6">
              <ImageIcon className="h-16 w-16 text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-3">No hay marcas</h3>
            <p className="text-slate-600 dark:text-slate-400 text-center mb-6 max-w-md">
              Comienza agregando tu primera marca para organizar mejor tus productos.
            </p>
            <Button onClick={() => setModalOpen(true)} className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white shadow-lg hover:shadow-xl transition-all duration-200">
              <Plus className="mr-2 h-5 w-5" />
              Crear Primera Marca
            </Button>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default Brands;
